import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeals } from '@/hooks/useMeals';
import { ConstraintCaptureForm } from './ConstraintCaptureForm';
import type { GeneratedSlot, MealType } from '@/types/api';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

type SlotStatus = 'pending' | 'accepted' | 'swapped' | 'skipped';

interface CompositionSlotProps {
  slot: GeneratedSlot;
  status: SlotStatus;
  selectedMealId: number | null;
  selectedMealName: string | null;
  disabled?: boolean;
  onAccept: () => void;
  onSkip: () => void;
  onSwap: (mealId: number, mealName: string) => void;
  onConstraintSaved?: () => void;
}

const statusStyles: Record<SlotStatus, string> = {
  pending: 'border-amber-300 bg-white',
  accepted: 'border-green-400 bg-green-50',
  swapped: 'border-blue-400 bg-blue-50',
  skipped: 'border-gray-300 bg-gray-50',
};

const mealTypeLabel = (mealType: MealType, t: (key: string) => string) =>
  mealType === 'lunch' ? t('availability.lunch') : t('availability.dinner');

export function CompositionSlot({
  slot,
  status,
  selectedMealId,
  selectedMealName,
  disabled = false,
  onAccept,
  onSkip,
  onSwap,
  onConstraintSaved,
}: CompositionSlotProps) {
  const { t } = useTranslation();
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [constraintTarget, setConstraintTarget] = useState<{
    userId: number;
    userName: string;
  } | null>(null);

  const searchEnabled = showSearch && searchQuery.length >= 2;
  const { data: searchResults } = useMeals(
    searchEnabled ? { query: searchQuery, limit: 10 } : undefined,
    { enabled: searchEnabled },
  );

  const dayLabel = format(parseISO(slot.date), 'EEEE', { locale: fr });
  const dateLabel = format(parseISO(slot.date), 'd MMM', { locale: fr });

  // Determine which meal to display
  const displayMealId = selectedMealId ?? slot.suggestion?.mealId ?? null;
  const displayMealName =
    selectedMealName ?? slot.suggestion?.mealName ?? null;

  // Show suggestion tags/justification only when displaying the original suggestion
  const isShowingSuggestion =
    displayMealId === slot.suggestion?.mealId && status !== 'swapped';
  const displayTags = isShowingSuggestion ? (slot.suggestion?.tags ?? []) : [];
  const displayJustification = isShowingSuggestion
    ? slot.suggestion?.justification
    : undefined;

  return (
    <>
      <div
        className={`rounded-lg border-2 p-4 transition-colors ${statusStyles[status]}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 capitalize">
              {dayLabel} — {mealTypeLabel(slot.mealType, t)}
            </h3>
            <p className="text-xs text-gray-500">{dateLabel}</p>
          </div>
          {status !== 'pending' && (
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${
                status === 'accepted'
                  ? 'bg-green-100 text-green-700'
                  : status === 'swapped'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
              }`}
            >
              {status === 'accepted'
                ? t('compose.accepted')
                : status === 'swapped'
                  ? t('compose.swapped')
                  : t('compose.skipped')}
            </span>
          )}
        </div>

        {/* Present members with "Not for" buttons */}
        <div className="flex flex-wrap gap-1 mb-3">
          {slot.presentUsers.map((user) => (
            <span
              key={user.id}
              className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
            >
              {user.name}
              {!disabled && displayMealId && (
                <button
                  onClick={() =>
                    setConstraintTarget({
                      userId: user.id,
                      userName: user.name,
                    })
                  }
                  className="text-red-400 hover:text-red-600 ml-0.5"
                  title={t('constraint.notFor', { name: user.name })}
                >
                  &times;
                </button>
              )}
            </span>
          ))}
        </div>

        {/* Suggestion / Selected meal */}
        {displayMealId ? (
          <div className="bg-white rounded-md p-3 mb-3 border border-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="font-medium text-gray-900">{displayMealName}</p>
                {displayTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {displayTags
                      .filter((tag) => tag !== 'hidden')
                      .slice(0, 4)
                      .map((tag) => (
                        <span
                          key={tag}
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            tag === 'healthy'
                              ? 'bg-green-100 text-green-700'
                              : tag === 'cheat'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                  </div>
                )}
              </div>
              {isShowingSuggestion && slot.suggestion && (
                <span className="text-xs text-gray-400 ml-2">
                  {slot.suggestion.score.toFixed(1)}
                </span>
              )}
            </div>
            {displayJustification && (
              <p className="text-xs text-gray-500 mt-2 italic">
                {displayJustification}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-md p-3 mb-3 border border-dashed border-gray-200 text-center">
            <p className="text-sm text-gray-400">
              {t('compose.noSuggestion')}
            </p>
          </div>
        )}

        {/* Action buttons */}
        {!disabled && (
          <div className="flex gap-2 mb-2">
            {displayMealId && (
              <button
                onClick={onAccept}
                disabled={status === 'accepted'}
                className={`flex-1 text-sm font-medium py-1.5 px-3 rounded-md transition-colors ${
                  status === 'accepted'
                    ? 'bg-green-100 text-green-700 cursor-default'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {t('compose.accept')}
              </button>
            )}
            <button
              onClick={() => setShowAlternatives(!showAlternatives)}
              className="flex-1 text-sm font-medium py-1.5 px-3 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              {showAlternatives
                ? t('compose.hideAlternatives')
                : t('compose.showAlternatives')}
            </button>
            <button
              onClick={onSkip}
              disabled={status === 'skipped'}
              className={`text-sm font-medium py-1.5 px-3 rounded-md transition-colors ${
                status === 'skipped'
                  ? 'bg-gray-200 text-gray-400 cursor-default'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {t('compose.skip')}
            </button>
          </div>
        )}

        {/* Alternatives list */}
        {showAlternatives && !disabled && slot.alternatives.length > 0 && (
          <div className="mt-2 space-y-1">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {t('compose.alternatives')}
            </p>
            {slot.alternatives.map((alt) => (
              <button
                key={alt.mealId}
                onClick={() => {
                  onSwap(alt.mealId, alt.mealName);
                  setShowAlternatives(false);
                }}
                className="w-full text-left p-2 rounded-md hover:bg-blue-50 border border-gray-100 transition-colors flex items-center justify-between"
              >
                <div>
                  <span className="text-sm font-medium text-gray-800">
                    {alt.mealName}
                  </span>
                  <div className="flex gap-1 mt-0.5">
                    {alt.tags
                      .filter((tag) => tag !== 'hidden')
                      .slice(0, 3)
                      .map((tag) => (
                        <span
                          key={tag}
                          className="text-xs bg-gray-100 text-gray-500 px-1 py-0.5 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                  </div>
                </div>
                <span className="text-xs text-gray-400">
                  {alt.score.toFixed(1)}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Meal search */}
        {!disabled && (
          <div className="mt-2">
            {!showSearch ? (
              <button
                onClick={() => setShowSearch(true)}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {t('compose.searchCatalogue')}
              </button>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('menu.searchMeals')}
                  className="w-full text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
                {searchEnabled &&
                  searchResults?.meals &&
                  searchResults.meals.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {searchResults.meals.map((meal) => (
                        <button
                          key={meal.id}
                          onClick={() => {
                            onSwap(meal.id, meal.name);
                            setShowSearch(false);
                            setSearchQuery('');
                          }}
                          className="w-full text-left p-2 rounded-md hover:bg-blue-50 border border-gray-100 text-sm"
                        >
                          {meal.name}
                        </button>
                      ))}
                    </div>
                  )}
                <button
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  {t('common.close')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Constraint capture modal */}
      {constraintTarget && displayMealId && displayMealName && (
        <ConstraintCaptureForm
          userId={constraintTarget.userId}
          userName={constraintTarget.userName}
          mealId={displayMealId}
          mealName={displayMealName}
          onSaved={() => {
            setConstraintTarget(null);
            onConstraintSaved?.();
          }}
          onCancel={() => setConstraintTarget(null)}
        />
      )}
    </>
  );
}
